/**
 * <juro-impact-dots>: the racket's halftone print, with an impact wave
 * ------------------------------------------------------------------
 * The production JURO FOCUS face is printed with a halftone: a hex grid of
 * dots that grow toward the sweet spot. This draws the same print across the
 * section and sends a ripple out from the centre through it, as if the ball
 * has just landed there. The wave only swells the dots; no colour is added.
 *
 * Canvas 2D, no library. Dots are grouped into a few alpha buckets so each
 * frame is a handful of fills. Stops offscreen and when the tab is hidden,
 * capped at 30fps, drawn once under reduced motion.
 *
 * Attribute: data-color, dot colour as a hex, default #0f1b2c.
 * Decorative: aria-hidden. Nothing but pixels is written.
 */

(() => {
  if (customElements.get('juro-impact-dots')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PERIOD = 4200; // ms between impacts
  const WAVES = 2; // wave fronts alive at once
  const BUCKETS = 10;

  const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    const n = parseInt(m ? m[1] : '0f1b2c', 16);
    return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  };
  const smooth = (t) => {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
  };

  class JuroImpactDots extends HTMLElement {
    connectedCallback() {
      if (this.canvas) return;
      this.canvas = document.createElement('canvas');
      this.canvas.style.cssText = 'display:block;inline-size:100%;block-size:100%';
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.rgb = hexToRgb(this.dataset.color);
      this.visible = false;
      this.last = 0;

      this.resize = this.resize.bind(this);
      this.frame = this.frame.bind(this);
      this.ro = new ResizeObserver(this.resize);
      this.ro.observe(this);
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.start();
      });
      this.io.observe(this);
      this.onVisibility = () => document.visibilityState === 'visible' && this.start();
      document.addEventListener('visibilitychange', this.onVisibility);
      reduced.addEventListener('change', this.resize);
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduced.removeEventListener('change', this.resize);
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    resize() {
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.w = w;
      this.h = h;

      // Hex grid, centred so one dot sits exactly on the sweet spot.
      const pitch = w < 600 ? 11 : 14;
      const rowH = pitch * 0.866;
      const cx = w / 2;
      const cy = h / 2;
      this.reach = Math.hypot(cx, cy);
      const halo = Math.min(w, h) * 0.62;
      const dots = [];
      const rows = Math.ceil(cy / rowH) + 1;
      const cols = Math.ceil(cx / pitch) + 1;
      for (let row = -rows; row <= rows; row++) {
        const y = cy + row * rowH;
        const shift = row & 1 ? pitch / 2 : 0;
        for (let col = -cols; col <= cols; col++) {
          const x = cx + col * pitch + shift;
          const d = Math.hypot(x - cx, y - cy);
          const k = smooth(1 - d / halo);
          dots.push({ x, y, d, base: pitch * (0.09 + 0.17 * k), alpha: 0.16 + 0.3 * k });
        }
      }
      this.dots = dots;
      this.pitch = pitch;
      this.draw(0);
      this.start();
    }

    start() {
      if (this.raf || reduced.matches || !this.visible || document.visibilityState !== 'visible') return;
      this.raf = requestAnimationFrame(this.frame);
    }

    frame(now) {
      this.raf = 0;
      if (reduced.matches || !this.visible || document.visibilityState !== 'visible') return;
      if (now - this.last >= 33) {
        this.last = now;
        this.draw(now);
      }
      this.raf = requestAnimationFrame(this.frame);
    }

    draw(now) {
      const { ctx, dots, pitch, reach } = this;
      if (!dots) return;
      ctx.clearRect(0, 0, this.w, this.h);

      const fronts = [];
      if (!reduced.matches) {
        for (let i = 0; i < WAVES; i++) {
          const t = ((now / PERIOD + i / WAVES) % 1 + 1) % 1;
          // eases out like a real ripple: fast off the centre, slow at the edge
          fronts.push({ r: (1 - Math.pow(1 - t, 2)) * reach * 1.05, fade: 1 - t });
        }
      }
      const band = pitch * 5;

      const paths = Array.from({ length: BUCKETS }, () => new Path2D());
      for (const dot of dots) {
        let swell = 0;
        for (const f of fronts) {
          const x = (dot.d - f.r) / band;
          swell = Math.max(swell, Math.exp(-x * x) * f.fade);
        }
        const r = dot.base + pitch * 0.16 * swell;
        const a = Math.min(1, dot.alpha + 0.5 * swell);
        const b = Math.min(BUCKETS - 1, Math.floor(a * BUCKETS));
        paths[b].moveTo(dot.x + r, dot.y);
        paths[b].arc(dot.x, dot.y, r, 0, Math.PI * 2);
      }
      paths.forEach((p, b) => {
        ctx.fillStyle = `rgba(${this.rgb},${((b + 0.5) / BUCKETS).toFixed(2)})`;
        ctx.fill(p);
      });
    }
  }

  customElements.define('juro-impact-dots', JuroImpactDots);
})();
